import React, { useState, useEffect, useCallback, useRef } from 'react';

// ============================================================
// CONFIGURATION
// ============================================================
// const API_BASE = 'http://127.0.0.1:8000/api/v1/analytics';
const API_BASE = `${process.env.REACT_APP_BASEURL}/analytics`;
const CSRF_TOKEN = '27ezJZPmpOdEFOKhrNqpOcK05hOrqu9S';

// ============================================================
// TOAST NOTIFICATION SYSTEM
// ============================================================
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    type === 'success'
      ? 'bg-emerald-500'
      : type === 'error'
      ? 'bg-rose-500'
      : type === 'warning'
      ? 'bg-amber-500'
      : 'bg-slate-700';

  const icon =
    type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️';

  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-white font-medium ${bgColor} animate-slide-in max-w-md`}
    >
      <span className="text-xl">{icon}</span>
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 text-white/70 hover:text-white transition-colors text-lg"
      >
        ✕
      </button>
    </div>
  );
};

// ============================================================
// CONFIRMATION DIALOG
// ============================================================
const ConfirmDialog = ({ isOpen, onClose, onConfirm, imageName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all animate-scale-in">
        <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-rose-100 text-rose-500 text-3xl mb-4">
          🗑️
        </div>
        <h3 className="text-xl font-bold text-center text-slate-800">Delete Image</h3>
        <p className="text-center text-slate-500 mt-2">
          Are you sure you want to delete{' '}
          <span className="font-semibold text-slate-700">{imageName}</span>?
          <br />
          <span className="text-sm text-rose-400">This action cannot be undone.</span>
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 rounded-xl bg-rose-500 text-white font-medium hover:bg-rose-600 transition-all shadow-lg shadow-rose-200"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// UPLOAD MODAL
// ============================================================
const UploadModal = ({ isOpen, onClose, onUpload, isUploading }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setPreview(null);
      setDragActive(false);
    }
  }, [isOpen]);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreview(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleSubmit = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl transform transition-all animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <span className="text-3xl">📤</span> Upload Image
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* Drop zone */}
        <div
          className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
            dragActive
              ? 'border-indigo-500 bg-indigo-50'
              : preview
              ? 'border-emerald-300 bg-emerald-50/50'
              : 'border-slate-300 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/50'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleFileSelect(file);
            }}
          />

          {preview ? (
            <div className="space-y-3">
              <img
                src={preview}
                alt="Preview"
                className="max-h-56 mx-auto rounded-xl shadow-md object-contain"
              />
              <p className="text-sm font-medium text-slate-700">{selectedFile?.name}</p>
              <p className="text-xs text-slate-400">
                {(selectedFile?.size / 1024).toFixed(1)} KB
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  setPreview(null);
                }}
                className="text-xs text-rose-400 hover:text-rose-600 font-medium"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-5xl">🖼️</div>
              <p className="font-medium text-slate-700">
                Drop your image here, or <span className="text-indigo-500">browse</span>
              </p>
              <p className="text-sm text-slate-400">PNG, JPG, WEBP up to 10MB</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedFile || isUploading}
            className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              !selectedFile || isUploading
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-500 text-white hover:bg-indigo-600 shadow-lg shadow-indigo-200'
            }`}
          >
            {isUploading ? (
              <>
                <span className="animate-spin">⏳</span> Uploading…
              </>
            ) : (
              '📤 Upload'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT: ImageUrls (Table Layout)
// ============================================================
function ImageUrls() {
  // ---------- State ----------
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Search
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Upload
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  // Copy feedback
  const [copiedId, setCopiedId] = useState(null);

  // ---------- Debounce search ----------
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // ---------- Fetch images ----------
  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });
      if (searchDebounced.trim()) {
        params.append('search', searchDebounced.trim());
      }

      const response = await fetch(`${API_BASE}/all-uploaded-images/?${params.toString()}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'X-CSRFTOKEN': CSRF_TOKEN,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const imageList = data.results || data.images || [];
      setImages(imageList);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
      setHasNext(data.has_next || false);
      setHasPrevious(data.has_previous || false);
    } catch (err) {
      setError(err.message);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchDebounced]);

  // ---------- Fetch on page/search change ----------
  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // ---------- Upload image ----------
  const handleUpload = async (file) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${API_BASE}/upload-image/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'X-CSRFTOKEN': CSRF_TOKEN,
        },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setToast({ message: `✅ "${data.file_name || file.name}" uploaded successfully!`, type: 'success' });
      setUploadModalOpen(false);
      fetchImages();
    } catch (err) {
      setToast({ message: `❌ Upload failed: ${err.message}`, type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  // ---------- Delete image ----------
  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;

    try {
      const response = await fetch(
        `${API_BASE}/upload-image/?id=${encodeURIComponent(imageToDelete.uid)}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
            'X-CSRFTOKEN': CSRF_TOKEN,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      setToast({
        message: `🗑️ "${imageToDelete.file_name}" deleted successfully!`,
        type: 'success',
      });
      setDeleteConfirmOpen(false);
      setImageToDelete(null);
      fetchImages();
    } catch (err) {
      setToast({ message: `❌ Delete failed: ${err.message}`, type: 'error' });
      setDeleteConfirmOpen(false);
    }
  };

  // ---------- Copy URL ----------
  const handleCopy = (url, id) => {
    const fallbackCopy = () => {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedId(id);
        setToast({ message: '📋 URL copied to clipboard!', type: 'success' });
        setTimeout(() => setCopiedId(null), 2000);
      } catch (err) {
        setToast({ message: '❌ Failed to copy URL', type: 'error' });
      }
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => {
          setCopiedId(id);
          setToast({ message: '📋 URL copied to clipboard!', type: 'success' });
          setTimeout(() => setCopiedId(null), 2000);
        })
        .catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  // ---------- Pagination helpers ----------
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ---------- Loading Skeleton ----------
  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
          <div className="flex-1 h-4 bg-slate-200 rounded w-1/4"></div>
          <div className="flex-1 h-4 bg-slate-200 rounded w-1/2"></div>
          <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
        </div>
      ))}
    </div>
  );

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-8 font-sans">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setImageToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        imageName={imageToDelete?.file_name || ''}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUpload}
        isUploading={isUploading}
      />

      {/* ----- HEADER ----- */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 flex items-center gap-3">
              <span className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white p-2.5 rounded-2xl shadow-lg shadow-indigo-200 text-2xl">
                🖼️
              </span>
              Image Gallery
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {totalItems > 0 ? `${totalItems} images` : 'No images'} • Page {page} of {totalPages}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="text"
                placeholder="Search images…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all w-44 sm:w-56 text-sm shadow-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-sm"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Upload button */}
            <button
              onClick={() => setUploadModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-medium shadow-lg shadow-indigo-200 hover:shadow-indigo-300 hover:scale-[1.02] transition-all"
            >
              <span>📤</span> Upload
            </button>
          </div>
        </div>

        {/* ----- TABLE ----- */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl shadow-slate-200/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gradient-to-r from-slate-50/80 to-indigo-50/80 backdrop-blur-sm">
                <tr>
                  <th scope="col" className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Image
                  </th>
                  <th scope="col" className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    File Name
                  </th>
                  <th scope="col" className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Image URL
                  </th>
                  <th scope="col" className="px-4 py-3.5 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="p-0">
                      <LoadingSkeleton />
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-12 text-center">
                      <div className="text-5xl mb-4">⚠️</div>
                      <p className="text-slate-600 font-medium">Failed to load images</p>
                      <p className="text-slate-400 text-sm mt-1">{error}</p>
                      <button
                        onClick={fetchImages}
                        className="mt-4 px-6 py-2.5 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-all shadow-md shadow-indigo-200"
                      >
                        Retry
                      </button>
                    </td>
                  </tr>
                ) : images.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-16 text-center">
                      <div className="text-6xl mb-4">🖼️</div>
                      <h3 className="text-xl font-semibold text-slate-700">No images yet</h3>
                      <p className="text-slate-400 mt-1">
                        {searchDebounced ? 'Try a different search term' : 'Upload your first image to get started'}
                      </p>
                      {searchDebounced && (
                        <button
                          onClick={() => setSearch('')}
                          className="mt-4 px-5 py-2 text-sm bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-all"
                        >
                          Clear search
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  images.map((image) => (
                    <tr key={image.id || image.uid} className="hover:bg-indigo-50/30 transition-colors duration-150 group">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <img
                          src={image.image_url}
                          alt={image.file_name}
                          className="w-12 h-12 rounded-lg object-cover shadow-sm ring-1 ring-slate-200/50"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-700">{image.file_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <a
                            href={image.image_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline truncate block transition-colors flex-1 min-w-0"
                            title={image.image_url}
                          >
                            {image.image_url}
                          </a>
                          <button
                            onClick={() => handleCopy(image.image_url, image.uid)}
                            className="flex-shrink-0 p-1 rounded hover:bg-indigo-50 transition-colors text-slate-400 hover:text-indigo-600"
                            title="Copy URL"
                          >
                            {copiedId === image.uid ? '✅' : '📋'}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleDeleteClick(image)}
                          className="p-2 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all"
                          title="Delete image"
                        >
                          <span className="text-lg">🗑️</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ----- PAGINATION ----- */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={!hasPrevious}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                hasPrevious
                  ? 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-indigo-300'
                  : 'bg-slate-100 text-slate-300 cursor-not-allowed'
              }`}
            >
              ← Prev
            </button>

            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                const isActive = pageNum === page;
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`w-10 h-10 rounded-xl font-medium transition-all ${
                      isActive
                        ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-200'
                        : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-indigo-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => goToPage(page + 1)}
              disabled={!hasNext}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                hasNext
                  ? 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-indigo-300'
                  : 'bg-slate-100 text-slate-300 cursor-not-allowed'
              }`}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ---------- Tailwind animations (injected via style) ---------- */}
      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-slide-in { animation: slide-in 0.4s ease-out; }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-scale-in { animation: scale-in 0.25s ease-out; }
      `}</style>
    </div>
  );
}

export default ImageUrls;